import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Seo, Reveal } from "../components/Seo";
import { PageHero, SectionHead, StatusPill, CtaBand } from "../components/blocks";
import { PRODUCTS } from "../lib/site";

export default function Products() {
  return (
    <>
      <Seo
        title="Digital Products — Softline Nigeria"
        description="SoftEbox secure digital mailbox, myID digital identity and CoFundApp community savings — proprietary products engineered by Softline Nigeria."
        path="/products"
      />
      <PageHero
        eyebrow="Digital products"
        title={["Products built for", "real-world challenges."]}
        lead="Proprietary platforms engineered by Softline — each addressing a specific operational problem, each labelled with its true readiness status."
        crumbs={[{ label: "Home", to: "/" }, { label: "Products" }]}
      />

      <section className="bg-white" data-testid="products-list">
        <div className="shell py-24 md:py-32 space-y-8">
          {PRODUCTS.map((p, i) => {
            const appScreenshot = p.screenshots?.recipient?.image || p.logo;

            return (
              <Reveal key={p.slug} delay={i * 0.06}>
                <article
                  data-testid={`products-card-${p.slug}`}
                  className="grid lg:grid-cols-12 border border-charcoal/10 hover:border-charcoal/30 transition-[border-color] duration-200 group"
                >
                  <Link
                    to={`/products/${p.slug}`}
                    className="lg:col-span-4 flex items-center justify-center p-6 md:p-8 overflow-hidden relative cursor-pointer"
                    style={{ background: p.logoBg }}
                    aria-label={`View ${p.name} product details and interface`}
                  >
                    {appScreenshot ? (
                      <div className="w-full aspect-[16/10] overflow-hidden rounded-[4px] border border-charcoal/15 shadow-md bg-white/10 group-hover:shadow-xl transition-shadow duration-300 flex items-center justify-center">
                        <img
                          src={appScreenshot}
                          alt={`${p.name} app interface`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className="text-center">
                        <span className="inline-flex items-center justify-center h-28 w-44 bg-[#0a2e24] border border-white/15 rounded-[4px] font-display font-bold text-white text-2xl">
                          {p.logoMonogram}
                        </span>
                      </div>
                    )}
                  </Link>
                <div className="lg:col-span-8 p-8 md:p-10 flex flex-col">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h2 className={`font-display font-bold text-2xl md:text-3xl ${p.darkBand ? "text-white" : "text-charcoal"}`}>{p.name}</h2>
                      <p className={`mt-2 font-mono text-[11px] uppercase tracking-[0.2em] ${p.darkBand ? "text-white/60" : "text-charcoal/55"}`}>{p.tagline}</p>
                    </div>
                    <StatusPill status={p.status} dark={p.darkBand} />
                  </div>
                  <p className="mt-5 text-charcoal/75 leading-relaxed max-w-2xl">{p.desc}</p>
                  <p className="mt-4 text-sm text-charcoal/60 border-l-2 border-lime pl-4">
                    <span className="font-semibold text-charcoal/75">Built for:</span> {p.useCase}
                  </p>
                  <div className="mt-8 pt-2">
                    <Link
                      to={`/products/${p.slug}`}
                      data-testid={`products-link-${p.slug}`}
                      className="inline-flex items-center gap-2 bg-lime text-charcoal font-semibold px-6 py-3.5 hover:bg-limelight transition-colors duration-200"
                    >
                      {p.status.cta} <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
            );
          })}

          <Reveal delay={0.1}>
            <div className="mt-6 bg-surface border border-charcoal/10 p-8 md:p-10" data-testid="products-readiness-note">
              <h3 className="font-display font-bold text-xl">How we label readiness</h3>
              <p className="mt-3 text-charcoal/70 leading-relaxed max-w-3xl">
                Status is approved by content owners before publication and never inferred from the existence of code or
                screenshots. MVP — Continuous Improvement means the product is in live use with early customers while its
                capabilities keep maturing. Features are distinguished on every product page between{" "}
                <span className="font-semibold">available</span> and <span className="font-semibold">roadmap</span>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
